/**
 * Viewport 계층: scroller 측정, 고정 높이 virtual range, scroll-into-view 보정.
 *
 * pinned group도 같은 scroller의 선두 normal flow에 있으므로 정규 virtual surface의
 * scroll 원점과 현재 노출 높이에서 pinned가 차지하는 구간을 제외한다.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
  type UIEvent as ReactUIEvent,
} from "react";

import type { ListViewRowGeometry } from "./listViewShell";
import type { ListViewEntry } from "./useListViewCollections";

export type ListViewVirtualRange = { start: number; end: number };

export const DEFAULT_OVERSCAN = 5;
const DEFAULT_UNMEASURED_VISIBLE_ROWS = 10;

/** 고정 pitch 목록의 viewport + overscan 범위(end exclusive). */
export function getListViewVirtualRange({
  itemCount,
  rowPitch,
  scrollTop,
  viewportHeight,
  overscan = DEFAULT_OVERSCAN,
}: {
  itemCount: number;
  /** 행 시작점 사이 거리(px). gap이 있으면 행 높이보다 크다. */
  rowPitch: number;
  scrollTop: number;
  viewportHeight: number;
  overscan?: number;
}): ListViewVirtualRange {
  if (
    !Number.isFinite(itemCount) ||
    !Number.isFinite(rowPitch) ||
    !Number.isFinite(scrollTop) ||
    !Number.isFinite(viewportHeight) ||
    itemCount <= 0 ||
    rowPitch <= 0
  ) {
    return { start: 0, end: 0 };
  }
  const normalizedItemCount = Math.max(0, Math.floor(itemCount));
  const visibleStart = Math.min(
    normalizedItemCount - 1,
    Math.max(0, Math.floor(Math.max(0, scrollTop) / rowPitch)),
  );
  const visibleEnd = Math.max(
    visibleStart + 1,
    Math.ceil((Math.max(0, scrollTop) + Math.max(0, viewportHeight)) / rowPitch),
  );
  const normalizedOverscan = Number.isFinite(overscan)
    ? Math.max(0, Math.floor(overscan))
    : DEFAULT_OVERSCAN;
  return {
    start: Math.max(0, visibleStart - normalizedOverscan),
    end: Math.min(normalizedItemCount, visibleEnd + normalizedOverscan),
  };
}

// viewport rect 차이로 보정해 offsetParent·DOM 중첩에 무관하게 동작시킨다.
function scrollItemIntoView(item: HTMLElement, container: HTMLElement | null) {
  if (!container) return;
  const itemRect = item.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  if (itemRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - itemRect.top;
  } else if (itemRect.bottom > containerRect.bottom) {
    container.scrollTop += itemRect.bottom - containerRect.bottom;
  }
}

export function useListViewViewport<T>({
  geometry,
  overscan,
  pinnedCount,
  regularCount,
  tabStopEntry,
}: {
  /** 가상화 모드의 균일한 행 기하. 비가상화면 null이며 range/scroll 보정을 건너뛴다. */
  geometry: ListViewRowGeometry | null;
  overscan: number;
  pinnedCount: number;
  regularCount: number;
  tabStopEntry: ListViewEntry<T> | undefined;
}): {
  scrollerRef: RefObject<HTMLDivElement | null>;
  handleScroll: (event: ReactUIEvent<HTMLDivElement>) => void;
  visibleRegularIndexes: number[];
  scrollVirtualIndexIntoView: (index: number) => void;
  scrollMountedItemIntoView: (element: HTMLElement) => void;
} {
  const virtualized = geometry !== null;
  const rowHeight = geometry?.rowHeight ?? null;
  const rowPitch = geometry?.rowPitch ?? null;
  const leadingInset = geometry?.leadingInset ?? 0;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [measuredViewportHeight, setMeasuredViewportHeight] = useState(0);

  // 실제 viewport(래퍼가 준 높이)를 관측해 virtual range를 계산한다.
  useEffect(() => {
    if (!virtualized) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => setMeasuredViewportHeight(scroller.clientHeight);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [virtualized]);

  // 밀도/외관 전환은 선두 inset을 제외한 행 pitch 좌표로 재기준한다.
  const previousGeometryRef = useRef({ rowPitch, leadingInset });
  useLayoutEffect(() => {
    const previous = previousGeometryRef.current;
    previousGeometryRef.current = { rowPitch, leadingInset };
    if (
      rowPitch === null ||
      previous.rowPitch === null ||
      (previous.rowPitch === rowPitch && previous.leadingInset === leadingInset)
    )
      return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const rebased =
      scroller.scrollTop === 0
        ? 0
        : Math.round(
            (Math.max(0, scroller.scrollTop - previous.leadingInset) / previous.rowPitch) *
              rowPitch +
              leadingInset,
          );
    scroller.scrollTop = rebased;
    setScrollTop(scroller.scrollTop);
  }, [rowPitch, leadingInset]);

  const effectiveViewportHeight =
    measuredViewportHeight || (rowPitch ?? 1) * DEFAULT_UNMEASURED_VISIBLE_ROWS;
  const pinnedFlowHeight = rowPitch !== null ? leadingInset + pinnedCount * rowPitch : 0;
  const regularScrollTop = Math.max(0, scrollTop - pinnedFlowHeight);
  const visiblePinnedHeight = Math.max(0, pinnedFlowHeight - scrollTop);
  const regularViewportHeight = Math.max(0, effectiveViewportHeight - visiblePinnedHeight);
  const virtualRange =
    rowPitch !== null
      ? getListViewVirtualRange({
          itemCount: regularCount,
          rowPitch,
          scrollTop: regularScrollTop,
          viewportHeight: regularViewportHeight,
          overscan,
        })
      : { start: 0, end: regularCount };
  const visibleRegularIndexes = Array.from(
    { length: Math.max(0, virtualRange.end - virtualRange.start) },
    (_, offset) => virtualRange.start + offset,
  );
  // roving tab stop 행은 range 밖이어도 mount를 보장해 Tab 진입점을 잃지 않는다.
  if (
    virtualized &&
    tabStopEntry &&
    !tabStopEntry.pinned &&
    !visibleRegularIndexes.includes(tabStopEntry.index)
  ) {
    visibleRegularIndexes.push(tabStopEntry.index);
    visibleRegularIndexes.sort((left, right) => left - right);
  }

  const scrollVirtualIndexIntoView = useCallback(
    (index: number) => {
      if (rowPitch === null || rowHeight === null) return;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const viewportHeight = scroller.clientHeight || effectiveViewportHeight;
      // 행 reveal은 gap이 아닌 실제 row border-box까지만 요구한다.
      const itemTop = pinnedFlowHeight + index * rowPitch;
      const itemBottom = itemTop + rowHeight;
      const viewportTop = scroller.scrollTop;
      const viewportBottom = viewportTop + viewportHeight;
      let nextScrollTop = viewportTop;
      if (itemTop < viewportTop) nextScrollTop = itemTop;
      else if (itemBottom > viewportBottom) nextScrollTop = itemBottom - viewportHeight;
      if (nextScrollTop !== viewportTop) {
        scroller.scrollTop = nextScrollTop;
        setScrollTop(nextScrollTop);
      }
    },
    [effectiveViewportHeight, pinnedFlowHeight, rowHeight, rowPitch],
  );

  const scrollMountedItemIntoView = useCallback(
    (element: HTMLElement) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const previousScrollTop = scroller.scrollTop;
      scrollItemIntoView(element, scroller);
      if (virtualized && scroller.scrollTop !== previousScrollTop) {
        setScrollTop(scroller.scrollTop);
      }
    },
    [virtualized],
  );

  function handleScroll(event: ReactUIEvent<HTMLDivElement>) {
    if (virtualized) setScrollTop(event.currentTarget.scrollTop);
  }

  return {
    scrollerRef,
    handleScroll,
    visibleRegularIndexes,
    scrollVirtualIndexIntoView,
    scrollMountedItemIntoView,
  };
}
